import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import NotificationBell from '../components/NotificationBell';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { KpiCard } from '../ui';

// Rows shown before the tile collapses. Sorted oldest-first upstream, so the cap hides the
// newest queues and an ageing one can never be pushed out of sight by a fresher arrival.
const QUEUE_CAP = 8;

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtINR = (n) => '₹' + num(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtL = (n) => '₹' + (num(n) / 100000).toFixed(2) + 'L';
// Quiet-modules tile: a bare ISO date is unreadable at a glance, and this row is meant to be
// read at a glance or not at all.
const fmtDay = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const STATUS_COLORS = { sample:'#9CA3AF', confirmed:'#3B3A8F', in_production:'#EF9F27', shipped:'#0891b2', complete:'#16a34a', cancelled:'#E22D2D' };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [gpos, setGpos] = useState([]);
  const [frs, setFrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [sourcing, setSourcing] = useState([]);
  const [stats, setStats] = useState({});
  const [taskSummary, setTaskSummary] = useState(null);
  const [finKpis, setFinKpis] = useState(null);
  const [dbSummary, setDbSummary] = useState(null);
  const [pending, setPending] = useState(null);        // pending-work tile: my queues only
  // The tile caps at 8 rows. The sort is oldest-first, so the cap drops the NEWEST queues —
  // never the most urgent. Expands in place rather than linking away: there is no "all queues"
  // page for it to link to, and a label that goes nowhere is worse than no label.
  const [showAllQueues, setShowAllQueues] = useState(false);
  const isMgmt = ['admin', 'management'].includes(user?.role);
  const isFin = ['admin', 'management', 'accounts'].includes(user?.role);
  useEffect(() => {
    if (isMgmt) api.get('/finance/kpis').then(r => setFinKpis(r.data.data)).catch(() => {});
    if (isFin) api.get('/finance/daybook/summary').then(r => setDbSummary(r.data.data)).catch(() => {});
  }, [isMgmt, isFin]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [o, g, f, p, s, st, ts, pw] = await Promise.all([
          api.get('/orders').catch(() => ({ data: { data: [] } })),
          api.get('/oe/gpos').catch(() => ({ data: { data: [] } })),
          api.get('/oe/frs').catch(() => ({ data: { data: [] } })),
          api.get('/procurement/pos').catch(() => ({ data: { data: [] } })),
          api.get('/procurement/sourcing').catch(() => ({ data: { data: [] } })),
          api.get('/dashboard/stats').catch(() => ({ data: { data: {} } })),
          api.get('/tasks/summary').catch(() => ({ data: { data: null } })),
          // Own request, own failure. A role with no queues gets an empty list, not an
          // error, and a failure here must not blank the rest of the dashboard.
          api.get('/dashboard/pending-work').catch(() => ({ data: { data: { queues: [], role_gaps: [], quiet_modules: [] } } })),
        ]);
        setOrders(o.data.data || []); setGpos(g.data.data || []); setFrs(f.data.data || []);
        setPos(p.data.data || []); setSourcing(s.data.data || []);
        setStats(st.data.data || {});
        setTaskSummary(ts.data.data);
        setPending(pw.data.data || { queues: [], role_gaps: [], quiet_modules: [] });
      } catch (err) { console.error('Dashboard error:', err); }
      finally { setLoading(false); }
    };
    fetchData();
    const h = () => fetchData();
    window.addEventListener('dashboard:refresh', h);
    return () => window.removeEventListener('dashboard:refresh', h);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // ── Derived metrics ────────────────────────────────────────────────────────
  const poValue = pos.reduce((s, p) => s + num(p.total_amount || num(p.quantity) * num(p.unit_price)), 0);
  const sourceProgress = (cat) => {
    const lines = sourcing.filter(l => l.category === cat);
    const req = lines.reduce((s, l) => s + num(l.required_qty), 0);
    const src = lines.reduce((s, l) => s + num(l.sourced_qty), 0);
    return { lines: lines.length, req, src, pct: req > 0 ? Math.round((src / req) * 100) : 0 };
  };
  const fabric = sourceProgress('fabric'), yarn = sourceProgress('yarn'), trims = sourceProgress('trims');

  const statusCounts = {};
  orders.forEach(o => { const k = o.status || 'unknown'; statusCounts[k] = (statusCounts[k] || 0) + 1; });
  const statusPie = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  const today = new Date(); const in30 = new Date(); in30.setDate(today.getDate() + 30);
  const upcoming = orders
    .filter(o => o.delivery_date && new Date(o.delivery_date) >= today && new Date(o.delivery_date) <= in30)
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));

  // GPOs grouped by month (created_at) — last 6 months, chronological.
  const monthsBack = 6;
  const monthBuckets = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    monthBuckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, month: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }), count: 0 });
  }
  const bucketByKey = Object.fromEntries(monthBuckets.map(b => [b.key, b]));
  gpos.forEach(g => {
    if (!g.created_at) return;
    const d = new Date(g.created_at);
    const k = `${d.getFullYear()}-${d.getMonth()}`;
    if (bucketByKey[k]) bucketByKey[k].count += 1;
  });

  const kpis = [
    { label: 'Total GPOs', value: gpos.length, sub: 'order execution', color: '#3B3A8F' },
    { label: 'Total FRS', value: frs.length, sub: 'fabric sheets', color: '#0891b2' },
    { label: 'Total POs', value: pos.length, sub: 'purchase orders', color: '#854F0B' },
    { label: 'Total PO Value', value: fmtL(poValue), sub: fmtINR(poValue), color: '#E22D2D' },
    { label: 'Pending Approvals', value: num(stats.pending_approvals), sub: 'GPOs awaiting buyer response', color: '#9A6B00' },
    { label: 'Active Job Work', value: num(stats.active_jobwork), sub: `${num(stats.jobwork_due_7d)} JWO(s) due in 7 days`, color: '#C2410C' },
  ];
  const failedTests = stats.failed_tests || [];

  const card = { background: '#fff', borderRadius: 8, padding: 20, border: '1px solid #E8E7E7', boxShadow: 'none' };
  const ProgressRow = ({ label, p, unit, color }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color: '#444' }}>{label} <span style={{ color: '#aaa', fontWeight: 400 }}>({p.lines} lines)</span></span>
        <span style={{ color: '#666' }}>{p.src.toLocaleString('en-IN')} / {p.req.toLocaleString('en-IN')} {unit} · <strong style={{ color }}>{p.pct}%</strong></span>
      </div>
      <div style={{ background: '#eee', borderRadius: 6, height: 10, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, p.pct)}%`, height: '100%', background: color, transition: 'width .3s' }} />
      </div>
    </div>
  );

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f8' }}><p style={{ color: '#666' }}>Loading Khyati Portal...</p></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        {/* ── QUIET MODULES ─────────────────────────────────────────────────
            Every other tile counts work that EXISTS. This one reports work that has
            STOPPED — the failure mode nothing else here watches for, and the one that
            hid a six-day goods-inwards outage in August because nobody attempted a
            receipt and silence read as "fine".

            ONE tile listing every quiet module, never one tile each: eight permanently
            green boxes stop being read by the second week. Absent when nothing is quiet,
            which is its normal state.

            Each row says what to DO, not only what is true. "No goods receipt in 6 days"
            is a fact; the person reading it may not know what the module is or who owns
            it. The instruction is the part that gets acted on. */}
        {pending?.quiet_modules?.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #F0C0C0', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#A32D2D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Modules that have gone quiet
            </div>
            {pending.quiet_modules.map(m => (
              <div key={m.module} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid #f4f4f8' }}>
                {/* Three states, three badges, never blended. Gone quiet is a fault; never
                    started is a rollout that did not land; CANNOT CHECK is the monitor
                    admitting it does not know — and that must never be shown as silence,
                    because silence here reads as healthy. */}
                <span style={{ flexShrink: 0, marginTop: 1, padding: '1px 7px', borderRadius: 9, fontSize: 10, fontWeight: 700,
                  background: m.status === 'cannot_check' ? '#FBF0DC' : m.status === 'never_started' ? '#EDEBF7' : '#FCEBEB',
                  color: m.status === 'cannot_check' ? '#9A6B00' : m.status === 'never_started' ? '#3B3A8F' : '#A32D2D' }}>
                  {m.status === 'cannot_check' ? 'cannot check'
                    : m.status === 'never_started' ? 'never started'
                    : `${m.days_quiet}${m.business_days ? ' working' : ''} day${m.days_quiet === 1 ? '' : 's'}`}
                </span>
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: '#1a1a2e' }}>
                    {m.label}
                    {m.status === 'cannot_check'
                      ? <span style={{ fontWeight: 400, color: '#666' }}> — this check is failing, so the module's state is unknown</span>
                      : m.status === 'never_started'
                        ? <span style={{ fontWeight: 400, color: '#666' }}> — has never recorded anything</span>
                        : <span style={{ fontWeight: 400, color: '#666' }}> — last was {fmtDay(m.last_activity)}</span>}
                  </div>
                  <div style={{ color: m.status === 'cannot_check' ? '#9A6B00' : '#A32D2D', marginTop: 2 }}>{m.action}</div>
                  {m.error && <div style={{ color: '#888', marginTop: 2, fontFamily: 'monospace', fontSize: 10 }}>{m.error}</div>}
                </div>
                {m.link && <a href={m.link} style={{ flexShrink: 0, fontSize: 11, color: '#3B3A8F', textDecoration: 'none', fontWeight: 600 }}>open →</a>}
              </div>
            ))}
          </div>
        )}

        {/* ── PENDING WORK ──────────────────────────────────────────────────
            Only the viewer's own queues, only non-empty ones, oldest first. An empty
            queue is absent rather than a zero — a screen full of zeros is how the real
            numbers stopped being read. Value appears only where money is involved and
            is never totalled across rows: face value, claim and sanctioned amount do
            not net into one figure. */}
        {pending && (pending.queues.length > 0 || pending.role_gaps.length > 0) && (
          <div style={{ background: '#fff', border: '1px solid #e6e6ef', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Waiting on you
            </div>
            {(showAllQueues ? pending.queues : pending.queues.slice(0, QUEUE_CAP)).map(q => (
              <div key={q.key} onClick={() => navigate(q.link)}
                style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f8', cursor: 'pointer' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#3B3A8F', minWidth: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {q.count.toLocaleString('en-IN')}
                </span>
                <span style={{ fontSize: 13, color: '#333', flex: 1 }}>{q.label}</span>
                {q.value != null && (
                  <span style={{ fontSize: 12, color: '#555' }}>
                    ₹{Math.round(q.value).toLocaleString('en-IN')}
                    <span style={{ color: '#999' }}> {q.value_basis}</span>
                  </span>
                )}
                {/* The age is the point: oldest first, and anything past a fortnight is
                    coloured, because 34 days is not the same fact as 3. */}
                <span style={{ fontSize: 12, fontWeight: 700, minWidth: 74, textAlign: 'right',
                  color: q.oldest_age >= 14 ? '#A32D2D' : q.oldest_age >= 7 ? '#B45309' : '#888' }}>
                  {q.oldest_age != null ? `${q.oldest_age}d oldest` : ''}
                </span>
              </div>
            ))}
            {/* Only when there is genuinely something hidden. A merchandiser holding 5 queues
                must never see "+0 more" — a control that reveals nothing teaches people the
                tile is broken. Collapsing again is offered once expanded. */}
            {pending.queues.length > QUEUE_CAP && (
              <div onClick={() => setShowAllQueues(v => !v)}
                style={{ padding: '8px 0 2px', fontSize: 12, fontWeight: 700, color: '#3B3A8F', cursor: 'pointer' }}>
                {showAllQueues
                  ? '− Show fewer'
                  : `+${pending.queues.length - QUEUE_CAP} more queue${pending.queues.length - QUEUE_CAP === 1 ? '' : 's'} waiting`}
              </div>
            )}
            {/* A role named in a gate that nobody holds. NOT reassigned silently — the work
                would move and the gap that stalled it would disappear from view. */}
            {pending.role_gaps.map(g => (
              <div key={g.role} style={{ marginTop: 10, background: '#FEF3C7', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: 12, color: '#8A5A00' }}>
                  <strong>{g.role}</strong> guards {g.guards} and has no active holder — anything routed there cannot be actioned by the person it was sent to.
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Failed FPT/GPT alert */}
        {failedTests.length > 0 && (
          <div style={{ background: '#FCEBEB', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 18px', marginBottom: 20 }}>
            <div style={{ fontWeight: 800, color: '#A32D2D', fontSize: 13, marginBottom: 4 }}>🚨 Failed Tests — action required</div>
            {failedTests.map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: '#7f1d1d' }}>
                GPO <strong style={{ fontFamily: 'monospace' }}>{t.gpo_no}</strong> — {String(t.report_type).toUpperCase()} FAILED{t.test_house ? ` at ${t.test_house}` : ''}
              </div>
            ))}
          </div>
        )}
        {/* Task widgets */}
        {taskSummary && (
          <div style={{ display: 'grid', gridTemplateColumns: taskSummary.team ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20 }}>
            <div style={{ ...card, cursor: 'pointer', borderLeft: '3px solid #3B3A8F' }} onClick={() => navigate('/tasks')}>
              <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>✅ My Tasks</div>
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div><span style={{ fontSize: 26, fontWeight: 800, color: '#3B3A8F' }}>{taskSummary.my.pending}</span><span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>pending</span></div>
                <div><span style={{ fontSize: 26, fontWeight: 800, color: taskSummary.my.overdue > 0 ? '#E22D2D' : '#166534' }}>{taskSummary.my.overdue}</span><span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>overdue</span></div>
              </div>
            </div>
            {taskSummary.team && (
              <div style={{ ...card, cursor: 'pointer', borderLeft: '3px solid #9A6B00' }} onClick={() => navigate('/tasks')}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>👥 Team Tasks</div>
                <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                  <div><span style={{ fontSize: 26, fontWeight: 800, color: taskSummary.team.overdue > 0 ? '#E22D2D' : '#166534' }}>{taskSummary.team.overdue}</span><span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>overdue</span></div>
                  <div><span style={{ fontSize: 26, fontWeight: 800, color: '#9A6B00' }}>{taskSummary.team.pending_audit}</span><span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>pending audit</span></div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Finance KPIs — admin/management only */}
        {isMgmt && finKpis && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
            {[
              ['💼 Total Sales This Month', finKpis.sales_this_month, '#3B3A8F', finKpis.sales_count === 0],
              ['Total Outstanding', finKpis.total_outstanding, '#A32D2D', finKpis.sales_count === 0],
              ['Total Payable', finKpis.total_payable, '#C2410C', finKpis.purchase_count === 0],
            ].map(([l, v, c, noData]) => (
              // No sales/purchase invoices in the system → show "no data yet", NOT a misleading ₹0.
              <div key={l} style={{ ...card, cursor: noData ? 'default' : 'pointer', borderLeft: `3px solid ${noData ? '#D8D8DE' : c}` }} onClick={() => !noData && navigate('/finance')}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{l}</div>
                {noData
                  ? <div style={{ fontSize: 15, fontWeight: 700, color: '#9A9AA0', marginTop: 8 }}>No data yet <span style={{ fontSize: 11, fontWeight: 400 }}>· no invoices recorded</span></div>
                  : <div style={{ fontSize: 26, fontWeight: 800, color: c, marginTop: 6 }}>{fmtINR(v)}</div>}
              </div>
            ))}
          </div>
        )}
        {/* Finance Day Book summary — admin / management / accounts */}
        {isFin && dbSummary && (
          <div onClick={() => navigate('/finance')} style={{ ...card, cursor: 'pointer', borderLeft: '3px solid #166534', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>💵 Cash &amp; Bank — Paid this month</div>
              <div style={{ fontSize: 11, color: dbSummary.total_actual > dbSummary.total_budget && dbSummary.total_budget > 0 ? '#A32D2D' : '#166534', fontWeight: 700 }}>
                {fmtINR(dbSummary.total_actual)} paid / {fmtINR(dbSummary.total_budget)} budget
              </div>
            </div>
            <div style={{ display: 'flex', gap: 28, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: '#854F0B' }}>{fmtINR(dbSummary.cash)}</span><span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>cash paid</span></div>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: '#0E7490' }}>{fmtINR(dbSummary.bank)}</span><span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>bank paid</span></div>
              {dbSummary.over_heads && dbSummary.over_heads.length > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#A32D2D', fontWeight: 700 }}>🔴 Over budget:</span>
                  {dbSummary.over_heads.slice(0, 3).map(h => <span key={h.name} style={{ fontSize: 11, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D', padding: '2px 8px', borderRadius: 8 }}>{h.name} (+{fmtINR(h.over_by)})</span>)}
                </div>
              )}
            </div>
          </div>
        )}
        {/* KPI cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, marginBottom: 20 }}>
          {kpis.map((k, i) => <KpiCard key={i} label={k.label} value={k.value} color={k.color} sub={k.sub} />)}
        </div>

        {/* GPOs by month (bar) */}
        <div style={{ ...card, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 8px', color: '#222' }}>GPOs Created by Month <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400 }}>(last 6 months)</span></h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthBuckets} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={{ stroke: '#ddd' }} />
              <YAxis allowDecimals={false} fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="count" name="GPOs" fill="#3B3A8F" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Charts row: Orders by status (pie) + Sourcing progress */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 16, marginBottom: 20 }}>
          <div style={card}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 8px', color: '#222' }}>Orders by Status</h2>
            {statusPie.length === 0 ? <p style={{ color: '#aaa', fontSize: 13 }}>No orders.</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={11}>
                    {statusPie.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || '#bbb'} />)}
                  </Pie>
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={card}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px', color: '#222' }}>Sourcing Progress</h2>
            <ProgressRow label="🧵 Fabric" p={fabric} unit="KG" color="#3B3A8F" />
            <ProgressRow label="🧶 Yarn" p={yarn} unit="KG" color="#166534" />
            <ProgressRow label="🪡 Trims" p={trims} unit="pcs" color="#E22D2D" />
            {fabric.lines + yarn.lines + trims.lines === 0 && <p style={{ color: '#aaa', fontSize: 13 }}>No sourcing lines yet.</p>}
          </div>
        </div>

        {/* Upcoming deliveries */}
        <div style={card}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px', color: '#222' }}>📦 Upcoming Deliveries <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400 }}>(next 30 days · {upcoming.length})</span></h2>
          {upcoming.length === 0 ? <p style={{ color: '#aaa', fontSize: 13 }}>No deliveries due in the next 30 days.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>
                {['Order', 'GPO', 'Buyer', 'Style', 'Qty', 'Delivery', 'Days Left', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 500, borderBottom: '1px solid #eee' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {upcoming.map((o, i) => {
                  const days = Math.ceil((new Date(o.delivery_date) - today) / 86400000);
                  return (
                    <tr key={o.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 ? '#FAFAFA' : '#FFFFFF' }}>
                      <td style={{ padding: '9px 12px', color: '#3B3A8F', fontWeight: 600 }}>{o.order_no}</td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#888' }}>{o.gpo_no || '—'}</td>
                      <td style={{ padding: '9px 12px' }}>{o.buyer_name}</td>
                      <td style={{ padding: '9px 12px', color: '#666' }}>{o.style_no}</td>
                      <td style={{ padding: '9px 12px' }}>{num(o.quantity).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '9px 12px', color: '#666' }}>{new Date(o.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: days <= 7 ? '#E22D2D' : days <= 14 ? '#854F0B' : '#166534' }}>{days}d</td>
                      <td style={{ padding: '9px 12px' }}><span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: (STATUS_COLORS[o.status] || '#bbb') + '22', color: STATUS_COLORS[o.status] || '#888' }}>{(o.status || '—').replace('_', ' ')}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
